/**
 * ProductDetailModal — MS61a + MS61c product-context surface.
 *
 * The "View" CTA's destination. Originally shipped (MS61a) with
 * single-image / single-paragraph rendering; MS61c expands to use
 * the new vendor_products columns introduced in MS61b:
 *
 *   gallery_image_urls   — multi-image carousel
 *   long_description     — full marketing copy below the short blurb
 *   voucher_terms        — what the voucher covers + redemption
 *   contents_list        — hamper item list (with allergen flags)
 *   partner_venues       — voucher redemption venues
 *   included_inclusions  — workshop "what's included" bullets
 *   policy_text          — refund / cancellation / extension policy
 *   availability_windows — workshop / spa bookable dates (date picker
 *                          renders when non-empty; full booking flow
 *                          is MS61d)
 *   parent_product_id    — sister-SKU collapse → variant picker
 *   variant_label        — picker label per variant
 *
 * Every new section graceful-degrades: empty array / null → render
 * nothing, no visible gap. So a freshly-loaded modal looks identical
 * to MS61a until the scraper (next PR) or a vendor portal populates
 * the new fields. Each vendor that gets data lights up automatically.
 *
 * Data flow:
 *   Card props (SkuCardData)   →   first paint (lean fields)
 *   useSkuDetail lazy fetch    →   second paint (rich fields)
 *
 * Why lazy fetch instead of fattening SkuCardData: the new columns
 * are MODAL-only. Bloating every card render with hamper_contents +
 * partner_venues + voucher_terms would push every chat / browse /
 * vendor list to ship 10× the bytes per SKU for data the user
 * doesn't see unless they open the modal. The hook is a single
 * Postgres round-trip cached by react-query for the session.
 *
 * Variant picker:
 *   - When the hydrated detail returns variants.length > 1, render
 *     a pill row of variant_labels above the price.
 *   - Selecting a variant updates the displayed price + the SKU
 *     passed to OrderConfirmSheet on "Send to X".
 *   - When the SKU has no variants, picker is hidden entirely.
 *
 * UX shape, accessibility, focus management, body-scroll lock —
 * unchanged from MS61a.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, Loader2, X } from "lucide-react";
import type { SkuCardData } from "@/components/chat/SkuCard";
import { captureInteraction } from "@/lib/captureInteraction";
import { formatSkuName, formatSkuPrice } from "@/lib/formatSkuName";
import { useSkuDetail, type SkuDetail } from "@/hooks/queries/useSkuDetail";
import { useProductAddons } from "@/hooks/queries/useProductAddons";
import { useProductUpgrades } from "@/hooks/queries/useProductUpgrades";
import type { CheckoutAddon, ProductCheckoutCameFrom } from "@/hooks/useProductCheckoutFlow";
import type { FulfillmentReturnContext } from "@/lib/fulfillmentReturn";
import { FulfillmentReturnLink } from "@/components/lembra/FulfillmentReturnLink";
import { baseSkuAllowsAddons, filterAddonsForBase } from "@/lib/addonPolicy";

const GRADIENTS = [
  "from-butter to-butter/40",
  "from-coral/30 to-butter",
  "from-navy/10 to-butter/60",
  "from-coral/20 to-navy/10",
  "from-butter/80 to-coral/15",
];

function gradientForId(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return GRADIENTS[h % GRADIENTS.length];
}

interface ProductDetailModalProps {
  sku: SkuCardData;
  recipientName?: string | null;
  /** Surface the modal was opened from. Drives telemetry + the
   *  cameFrom field on the downstream OrderConfirmSheet. MS64a
   *  added wishlist / dashboard so the authed surfaces (Wishlist
   *  page, Dashboard saved-gifts cluster) can flow through the
   *  same modal as the unauthed flow. */
  cameFrom: ProductCheckoutCameFrom;
  fulfillmentReturn?: FulfillmentReturnContext | null;
  onSend: (sku: SkuCardData, addons?: CheckoutAddon[]) => void;
  onClose: () => void;
}

export function ProductDetailModal({
  sku,
  recipientName,
  cameFrom,
  fulfillmentReturn = null,
  onSend,
  onClose,
}: ProductDetailModalProps) {
  const closeRef = useRef<HTMLButtonElement>(null);

  // MS61c — lazy-fetch the full vendor_products row including the
  // enrichment columns. The modal renders the lean card fields
  // immediately (sku prop = first paint) and overlays the rich
  // fields when the fetch resolves.
  const detailQuery = useSkuDetail(sku.sku_id);
  const detail: SkuDetail | null = detailQuery.data?.sku ?? null;
  // MS141: same useMemo perf pattern as MS136-140. Pre-MS141
  // `detailQuery.data?.variants ?? []` was a fresh array each render
  // → the `selectedVariant` useMemo below re-ran every render even
  // when the underlying variants list hadn't changed.
  const variants = useMemo(
    () => detailQuery.data?.variants ?? [],
    [detailQuery.data?.variants],
  );
  const hasMultipleVariants = variants.length > 1;

  // Selected variant — initialises to whichever variant the buyer
  // clicked. When the modal opens with no variant data yet, this
  // stays as the originally-clicked SKU's detail (after fetch).
  const [selectedVariantId, setSelectedVariantId] = useState<string>(
    sku.sku_id,
  );
  const [selectedAddonIds, setSelectedAddonIds] = useState<Set<string>>(
    () => new Set(),
  );
  /** Type C tier upgrade — replaces the base line item when set. */
  const [appliedUpgrade, setAppliedUpgrade] = useState<SkuDetail | null>(null);

  const selectedVariant: SkuDetail | null = useMemo(() => {
    if (!hasMultipleVariants) return detail;
    return variants.find((v) => v.id === selectedVariantId) ?? detail;
  }, [variants, selectedVariantId, hasMultipleVariants, detail]);

  const displayIsVoucher = selectedVariant?.is_voucher ?? sku.is_voucher;
  const activeProductId = selectedVariantId || sku.sku_id;
  const activeBaseForAddons = {
    name: selectedVariant?.name ?? sku.name,
    slug: selectedVariant?.slug ?? sku.slug,
    is_voucher: displayIsVoucher,
  };
  const addonsQuery = useProductAddons(
    activeProductId,
    baseSkuAllowsAddons(activeBaseForAddons) && !appliedUpgrade,
  );
  const addonOptions = filterAddonsForBase(activeBaseForAddons, addonsQuery.data ?? []);

  const upgradeBaseId = appliedUpgrade?.id ?? activeProductId;
  const upgradeBasePrice =
    appliedUpgrade?.price ?? selectedVariant?.price ?? sku.price_aud;
  const upgradesQuery = useProductUpgrades(
    upgradeBaseId,
    upgradeBasePrice,
    displayIsVoucher,
  );
  const upgradeOptions = upgradesQuery.data ?? [];

  useEffect(() => {
    setSelectedAddonIds(new Set());
    setAppliedUpgrade(null);
  }, [activeProductId]);

  useEffect(() => {
    setAppliedUpgrade(null);
  }, [selectedVariantId]);

  const lineItemDetail = appliedUpgrade ?? selectedVariant;
  const displayPrice = lineItemDetail?.price ?? sku.price_aud;
  const displayVendorName =
    lineItemDetail?.vendor_name ?? detail?.vendor_name ?? sku.vendor_name;
  const displayVendorCategory =
    lineItemDetail?.vendor_category ?? selectedVariant?.vendor_category ?? sku.vendor_category;
  const displayName = formatSkuName({
    name: lineItemDetail?.name ?? selectedVariant?.name ?? sku.name,
    vendor_name: displayVendorName,
    vendor_category: displayVendorCategory,
    is_voucher: displayIsVoucher,
  });

  // Image set: hero image first, then any gallery images. Always
  // de-duplicates so a vendor who put the hero into gallery_image_urls
  // too doesn't ghost the carousel with a repeat.
  const imageUrls = useMemo(() => {
    const out: string[] = [];
    const heroUrl = lineItemDetail?.image_url ?? sku.image_url ?? null;
    if (heroUrl) out.push(heroUrl);
    const gallery = lineItemDetail?.gallery_image_urls ?? [];
    for (const u of gallery) {
      if (u && !out.includes(u)) out.push(u);
    }
    return out;
  }, [lineItemDetail, sku.image_url]);

  // MS6xx — single consolidated prose block. The modal used to stack
  // "About" (description) AND "The full story" (long_description),
  // but description is just the opening sentence of long_description
  // — the two restated each other, so the modal read twice as long
  // as it needed to. Collapsed to one block: the long copy when
  // present, the short blurb otherwise. Variant-aware (lineItemDetail
  // first) so it updates with tier switches, same as MS476. `find`
  // (not `??`) so an empty-string column can't win over real copy.
  const aboutText =
    [
      lineItemDetail?.long_description,
      detail?.long_description,
      lineItemDetail?.description,
      sku.description,
    ].find((t) => t && t.trim().length > 0) ?? null;

  // Focus, ESC, body-scroll lock — unchanged from MS61a.
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    void captureInteraction({
      surface: "product_detail",
      event_type: "viewed",
      payload: { sku_id: sku.sku_id, came_from: cameFrom },
      vendor_product_id: sku.sku_id,
    });
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = originalOverflow;
    };
    // sku.sku_id only — re-running on every parent re-render would
    // re-fire the capture event and re-focus the close button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sku.sku_id]);

  const handleSendClick = () => {
    const checkoutDetail = appliedUpgrade ?? selectedVariant;
    const targetSku: SkuCardData = checkoutDetail
      ? {
          sku_id: checkoutDetail.id,
          name: checkoutDetail.name,
          vendor_name: displayVendorName,
          vendor_category: checkoutDetail.vendor_category ?? sku.vendor_category,
          price_aud: checkoutDetail.price,
          image_url: checkoutDetail.image_url,
          slug: checkoutDetail.slug ?? sku.slug,
          description: checkoutDetail.description,
          is_voucher: checkoutDetail.is_voucher,
          lead_time_days: checkoutDetail.lead_time_days ?? sku.lead_time_days ?? null,
          dietary_tags: checkoutDetail.dietary_tags ?? sku.dietary_tags ?? null,
          why_this_one: sku.why_this_one,
          highlight_fields: sku.highlight_fields,
        }
      : sku;

    const selectedAddons: CheckoutAddon[] = addonOptions
      .filter((a) => selectedAddonIds.has(a.sku_id))
      .map((a) => ({
        sku_id: a.sku_id,
        name: a.name,
        price_aud: a.price_aud,
        vendor_name: a.vendor_name,
        is_voucher: a.is_voucher,
      }));

    void captureInteraction({
      surface: "product_detail",
      event_type: "send_clicked",
      payload: {
        sku_id: targetSku.sku_id,
        came_from: cameFrom,
        variant_selected: targetSku.sku_id !== sku.sku_id,
        upgrade_selected: Boolean(appliedUpgrade),
        addon_count: selectedAddons.length,
      },
      vendor_product_id: targetSku.sku_id,
    });
    onSend(targetSku, selectedAddons);
  };

  const addonsTotal = addonOptions
    .filter((a) => selectedAddonIds.has(a.sku_id))
    .reduce((sum, a) => sum + a.price_aud, 0);
  const combinedTotal = displayPrice + addonsTotal;

  const toggleAddon = (addonId: string) => {
    setSelectedAddonIds((prev) => {
      const next = new Set(prev);
      if (next.has(addonId)) next.delete(addonId);
      else next.add(addonId);
      return next;
    });
  };

  const firstName = recipientName?.trim().split(/\s+/)[0] ?? "";
  const sendLabel = firstName
    ? `Send to ${firstName}`
    : "Send to recipient";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="product-detail-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className="fixed inset-0 z-50 flex items-end justify-center bg-navy/40 backdrop-blur-sm sm:items-center sm:p-4"
      style={{ fontFamily: "DM Sans, sans-serif" }}
    >
      {/* MS202: was `max-h-[92vh] sm:max-h-[88vh]`. On iOS Safari
          `vh` is computed against the viewport WITH the address bar
          visible, so the modal could render taller than the actually-
          visible window — and as the address bar retracts/expands on
          scroll the modal didn't reflow, leaving its bottom either
          clipped under the bar or sitting in dead space. `dvh`
          tracks the live visible viewport. Same fix as MS201 applied
          to the inline-concierge thread + landing hero. */}
      <div className="flex w-full max-h-[92dvh] max-w-2xl flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl sm:max-h-[88dvh] sm:rounded-2xl">
        {/* Header: optional return + close */}
        <div className="flex items-center justify-between gap-3 border-b border-charcoal/8 px-4 py-2.5">
          {fulfillmentReturn ? (
            <FulfillmentReturnLink
              context={{
                ...fulfillmentReturn,
                onReturn: () => {
                  fulfillmentReturn.onReturn();
                  onClose();
                },
              }}
            />
          ) : (
            <span className="w-px" aria-hidden />
          )}
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close product details"
            /* MS203: was h-9 w-9 (36px). Below the 44px tap-target
               floor. Header row's py-2.5 grows by 8px to fit, which
               is invisible because the header has no other content
               competing for the row. */
            className="inline-flex h-11 w-11 items-center justify-center rounded-full text-navy/55 transition-colors hover:bg-charcoal/8 hover:text-navy focus:outline-none focus:ring-2 focus:ring-coral focus:ring-offset-2"
          >
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">
          {/* Image carousel — single image when no gallery, swipeable
              row when 2+. Snap-scroll for thumb friendliness; tiny
              page dots beneath. */}
          <ImageCarousel
            urls={imageUrls}
            altText={`${displayName}, ${displayVendorCategory} from ${displayVendorName}`}
            placeholderId={sku.sku_id}
            placeholderCategory={displayVendorCategory ?? "gift"}
          />

          <div className="space-y-5 px-5 py-6 sm:px-7 sm:py-7">
            {/* Vendor eyebrow */}
            <p className="text-xs uppercase tracking-[0.16em] text-charcoal/55">
              {displayVendorName}
            </p>

            {/* Name + price */}
            <div>
              <h2
                id="product-detail-title"
                className="text-2xl text-navy sm:text-3xl"
                style={{
                  fontFamily: "Newsreader, Georgia, serif",
                  fontWeight: 500,
                  lineHeight: 1.15,
                  letterSpacing: "-0.012em",
                }}
              >
                {displayName}
              </h2>
              <p
                className="mt-1.5 text-base font-semibold text-navy"
                style={{ fontFamily: "DM Sans, sans-serif" }}
              >
                {formatSkuPrice(displayPrice, displayIsVoucher)}
              </p>
            </div>

            {/* Variant picker — only when 2+ variants exist. */}
            {hasMultipleVariants && (
              <VariantPicker
                variants={variants}
                selectedId={selectedVariantId}
                isVoucher={displayIsVoucher}
                onSelect={(id) => setSelectedVariantId(id)}
              />
            )}
            {displayIsVoucher && hasMultipleVariants && (
              <p
                className="mt-2 text-xs text-charcoal/55"
                style={{ fontFamily: "DM Sans, sans-serif" }}
              >
                Need more credit? Choose a higher value above. Tier changes replace the base gift, they are not add-ons.
              </p>
            )}

            {upgradeOptions.length > 0 && (
              <UpgradePicker
                options={upgradeOptions}
                basePrice={appliedUpgrade?.price ?? selectedVariant?.price ?? sku.price_aud}
                selectedId={appliedUpgrade?.id ?? null}
                onSelect={(upgrade) => {
                  setAppliedUpgrade(upgrade);
                  setSelectedAddonIds(new Set());
                }}
                onClear={() => setAppliedUpgrade(null)}
              />
            )}
            {appliedUpgrade && (
              <button
                type="button"
                onClick={() => setAppliedUpgrade(null)}
                className="text-xs font-medium text-coral underline decoration-transparent underline-offset-2 hover:decoration-coral"
              >
                Revert to{" "}
                {formatSkuName({
                  name: selectedVariant?.name ?? sku.name,
                  vendor_name: displayVendorName,
                  vendor_category: displayVendorCategory,
                  is_voucher: displayIsVoucher,
                })}
              </button>
            )}

            {/* Highlight chips (kept from MS61a — they came from
                the chat carousel). */}
            {sku.highlight_fields.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {sku.highlight_fields.map((hf) => (
                  <span
                    key={`${hf.label}:${hf.value}`}
                    className="inline-flex items-center gap-1 rounded-full bg-charcoal/5 px-2.5 py-1 text-xs text-charcoal/75"
                  >
                    <span className="font-medium text-navy/75">{hf.label}:</span>
                    <span>{hf.value}</span>
                  </span>
                ))}
              </div>
            )}

            {/* Why-this-one — preserves the chat rationale. */}
            {sku.why_this_one && (
              <EditorialSection eyebrow="Why we picked this">
                <p
                  className="text-sm text-navy/75"
                  style={{
                    fontFamily: "Newsreader, Georgia, serif",
                    fontStyle: "italic",
                    lineHeight: 1.55,
                  }}
                >
                  {sku.why_this_one}
                </p>
              </EditorialSection>
            )}

            {/* About / What this voucher covers — single prose block.
                Was two stacked sections ("About" = description,
                "The full story" = long_description); see aboutText
                above for why they collapsed into one. Variant-aware
                via aboutText so the copy still updates when the buyer
                switches price tiers (MS476). */}
            {aboutText && (
              <EditorialSection
                eyebrow={displayIsVoucher ? "What this voucher covers" : "About"}
              >
                <ParaText text={aboutText} />
              </EditorialSection>
            )}

            {displayIsVoucher && (lineItemDetail?.voucher_terms ?? detail?.voucher_terms) ? (
              <EditorialSection eyebrow="Redemption">
                <ParaText text={(lineItemDetail?.voucher_terms ?? detail?.voucher_terms)!} />
              </EditorialSection>
            ) : displayIsVoucher && !(lineItemDetail?.description ?? sku.description) && !(lineItemDetail?.long_description ?? detail?.long_description) ? (
              <p
                className="rounded-xl border border-charcoal/8 bg-cream/40 px-4 py-3 text-xs text-navy/55"
                style={{ fontFamily: "DM Sans, sans-serif" }}
              >
                Voucher redemption details are confirmed with the
                vendor at checkout. The recipient gets a digital code
                + redemption instructions via email.
              </p>
            ) : null}

            {/* Contents list — MS61b. Hamper SKUs. */}
            {detail?.contents_list && detail.contents_list.length > 0 && (
              <EditorialSection eyebrow="What's inside">
                <ul className="space-y-1.5 text-sm text-navy/80">
                  {detail.contents_list.map((item, i) => (
                    <li key={i} className="flex items-baseline gap-2">
                      <span className="font-medium text-navy">
                        {item.quantity ? `${item.quantity}× ` : ""}
                        {item.label}
                      </span>
                      {item.note && (
                        <span className="text-xs text-charcoal/55">
                          ({item.note})
                        </span>
                      )}
                      {item.allergen_flags && item.allergen_flags.length > 0 && (
                        <span className="text-[10px] uppercase tracking-[0.1em] text-coral/85">
                          {item.allergen_flags.join(" · ")}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </EditorialSection>
            )}

            {/* Inclusions — MS61b. Workshops / experiences. */}
            {detail?.included_inclusions && detail.included_inclusions.length > 0 && (
              <EditorialSection eyebrow="Included">
                <ul className="space-y-1 text-sm text-navy/80">
                  {detail.included_inclusions.map((inc, i) => (
                    <li key={i} className="before:mr-2 before:text-coral before:content-['·']">
                      {inc}
                    </li>
                  ))}
                </ul>
              </EditorialSection>
            )}

            {/* Partner venues — MS61b. Dining / experience vouchers. */}
            {detail?.partner_venues && detail.partner_venues.length > 0 && (
              <EditorialSection eyebrow="Redeem at">
                <ul className="space-y-2 text-sm text-navy/80">
                  {detail.partner_venues.map((v, i) => (
                    <li key={i}>
                      <span className="font-medium text-navy">{v.name}</span>
                      {v.suburb && (
                        <span className="text-charcoal/55"> · {v.suburb}</span>
                      )}
                      {v.address && (
                        <div className="text-xs text-charcoal/65">{v.address}</div>
                      )}
                      {v.hours && (
                        <div className="text-xs text-charcoal/55">{v.hours}</div>
                      )}
                      {v.url && (
                        <a
                          href={v.url}
                          target="_blank" rel="noopener noreferrer"
                          className="text-xs text-coral underline decoration-transparent underline-offset-2 hover:decoration-coral"
                        >
                          Visit
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </EditorialSection>
            )}

            {/* Availability windows — MS61b. Workshop / spa bookable
                dates. Renders a date list (not yet a full booking
                picker — booking flow is MS61d). */}
            {detail?.availability_windows && detail.availability_windows.length > 0 && (
              <EditorialSection eyebrow="Available">
                <ul className="space-y-1 text-sm text-navy/80">
                  {detail.availability_windows.slice(0, 5).map((w, i) => (
                    <li key={i} className="flex items-baseline gap-2">
                      <span className="font-medium text-navy">
                        {formatBookingWindow(w.start_at, w.end_at)}
                      </span>
                      {typeof w.capacity_remaining === "number" && (
                        <span className="text-xs text-charcoal/55">
                          {w.capacity_remaining > 0
                            ? `${w.capacity_remaining} of ${w.capacity} seats left`
                            : "Sold out"}
                        </span>
                      )}
                      {w.sku_variant && (
                        <span className="text-[10px] uppercase tracking-[0.1em] text-charcoal/55">
                          {w.sku_variant}
                        </span>
                      )}
                    </li>
                  ))}
                  {detail.availability_windows.length > 5 && (
                    <li className="text-xs text-charcoal/55">
                      + {detail.availability_windows.length - 5} more dates available
                    </li>
                  )}
                </ul>
              </EditorialSection>
            )}

            {/* MS594: same-vendor optional add-ons */}
            {addonOptions.length > 0 && (
              <EditorialSection eyebrow="Make it extra special">
                <ul className="space-y-2">
                  {addonOptions.map((addon) => {
                    const checked = selectedAddonIds.has(addon.sku_id);
                    return (
                      <li key={addon.sku_id}>
                        <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-charcoal/10 bg-cream/30 px-3 py-2.5 hover:border-coral/30">
                          <input
                            type="checkbox"
                            className="mt-1 h-4 w-4 rounded border-charcoal/30 text-coral focus:ring-coral"
                            checked={checked}
                            onChange={() => toggleAddon(addon.sku_id)}
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-medium text-navy">
                              {formatSkuName({
                                name: addon.name,
                                vendor_name: addon.vendor_name,
                                vendor_category: addon.vendor_category,
                                is_voucher: addon.is_voucher,
                              })}
                              {addon.is_recommended ? (
                                <span className="ml-2 text-[10px] uppercase tracking-wider text-coral">
                                  Recommended
                                </span>
                              ) : null}
                            </span>
                            {addon.description ? (
                              <span className="mt-0.5 block text-xs text-charcoal/60 line-clamp-2">
                                {addon.description}
                              </span>
                            ) : null}
                          </span>
                          <span className="shrink-0 text-sm font-semibold text-navy">
                            +{formatSkuPrice(addon.price_aud, addon.is_voucher)}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </EditorialSection>
            )}

            {/* Policy text — MS61b. Quiet footer block. */}
            {detail?.policy_text && (
              <EditorialSection eyebrow="Policy">
                <ParaText text={detail.policy_text} />
              </EditorialSection>
            )}

            {/* Subtle loading indicator while the detail fetch
                runs — only visible on cold-cache open, blink-and-
                miss-it on cached re-open. */}
            {detailQuery.isLoading && !detail && (
              <div
                className="flex items-center gap-2 text-xs text-charcoal/45"
                style={{ fontFamily: "DM Sans, sans-serif" }}
              >
                <Loader2 size={12} className="animate-spin" />
                Loading more details…
              </div>
            )}
          </div>
        </div>

        {/* Sticky CTA footer */}
        <div className="border-t border-charcoal/10 bg-white px-5 py-3.5 sm:px-7 sm:py-4">
          {addonsTotal > 0 && (
            <p className="mb-2 text-center text-xs text-charcoal/65">
              Total with add-ons:{" "}
              <span className="font-semibold text-navy">
                {formatSkuPrice(combinedTotal, displayIsVoucher)}
              </span>
            </p>
          )}
          <button
            type="button"
            onClick={handleSendClick}
            className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-coral text-base font-semibold text-white transition-colors hover:bg-coral/85 focus:outline-none focus:ring-2 focus:ring-coral focus:ring-offset-2"
            style={{ fontFamily: "DM Sans, sans-serif" }}
            aria-label={`${sendLabel} (opens the order form)`}
          >
            {sendLabel}
            <ArrowRight size={16} strokeWidth={2.5} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Local subcomponents ────────────────────────────────────────────

/** Editorial section wrapper — mono uppercase eyebrow above the
 *  content. Used for description, voucher terms, contents list,
 *  partner venues, inclusions, policy. DRY's the repeated pattern
 *  from MS61a where the same eyebrow styling was duplicated. */
function EditorialSection({
  eyebrow,
  children,
}: {
  eyebrow: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h3
        className="mb-2 text-[10px] uppercase tracking-[0.18em] text-charcoal/55"
        style={{
          fontFamily:
            "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
        }}
      >
        {eyebrow}
      </h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

/** Multi-paragraph text — splits on \n\n. Reused for description,
 *  long_description, voucher_terms, policy_text. */
function ParaText({ text }: { text: string }) {
  return (
    <>
      {text.split(/\n{2,}/).map((para, i) => (
        <p
          key={i}
          className="text-sm leading-relaxed text-navy/80"
          style={{ fontFamily: "DM Sans, sans-serif" }}
        >
          {para}
        </p>
      ))}
    </>
  );
}

/** Image carousel. Snap-scroll horizontal row when there are 2+
 *  images; single image when 1; gradient placeholder when 0. The
 *  page-dots beneath update as the user swipes via an
 *  IntersectionObserver — keeps the scrolling silky on touch and
 *  the dots accurate without per-frame measurement. */
function ImageCarousel({
  urls,
  altText,
  placeholderId,
  placeholderCategory,
}: {
  urls: string[];
  altText: string;
  placeholderId: string;
  placeholderCategory: string;
}) {
  const [activeIdx, setActiveIdx] = useState(0);
  const trackRef = useRef<HTMLDivElement>(null);

  // Observe each slide; the one with the highest intersection ratio
  // is "the active slide" for dot purposes. threshold: 0.5 means a
  // slide needs to be at least half on-screen to count — prevents
  // mid-swipe flickering.
  useEffect(() => {
    if (urls.length <= 1) return;
    const track = trackRef.current;
    if (!track) return;
    const slides = Array.from(track.querySelectorAll<HTMLElement>("[data-slide-idx]"));
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) {
          const idx = Number(
            (visible.target as HTMLElement).dataset.slideIdx ?? "0",
          );
          setActiveIdx(idx);
        }
      },
      { root: track, threshold: 0.5 },
    );
    slides.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, [urls.length]);

  if (urls.length === 0) {
    // Placeholder — same gradient logic as MS61a.
    return (
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-charcoal/5 sm:aspect-[16/9]">
        <div
          aria-label={altText}
          role="img"
          className={`h-full w-full bg-gradient-to-br ${gradientForId(placeholderId)} flex items-end p-5`}
        >
          <span
            className="text-sm font-medium uppercase tracking-wider text-navy/55"
            style={{ letterSpacing: "0.12em" }}
          >
            {placeholderCategory}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <div
        ref={trackRef}
        className="flex aspect-[4/3] w-full snap-x snap-mandatory overflow-x-auto sm:aspect-[16/9] scrollbar-hide"
      >
        {urls.map((src, i) => (
          <div
            key={i}
            data-slide-idx={i}
            className="relative flex-none basis-full snap-start overflow-hidden bg-charcoal/5"
          >
            <img
              src={src}
              alt={`${altText}, image ${i + 1} of ${urls.length}`}
              loading={i === 0 ? "eager" : "lazy"}
              decoding="async"
              className="h-full w-full object-cover"
            />
          </div>
        ))}
      </div>

      {/* Page dots — only when there are 2+ images. Bottom-centred,
          quiet so the image stays the focal point. */}
      {urls.length > 1 && (
        <div
          className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5"
          aria-hidden="true"
        >
          {urls.map((_, i) => (
            <span
              key={i}
              className={`inline-block h-1.5 w-1.5 rounded-full transition-all ${
                i === activeIdx
                  ? "w-4 bg-white"
                  : "bg-white/55"
              }`}
              style={{ boxShadow: "0 0 8px rgba(0,0,0,0.25)" }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Type C upgrade picker — replaces the base SKU on selection. */
function UpgradePicker({
  options,
  basePrice,
  selectedId,
  onSelect,
  onClear,
}: {
  options: SkuDetail[];
  basePrice: number;
  selectedId: string | null;
  onSelect: (upgrade: SkuDetail) => void;
  onClear: () => void;
}) {
  return (
    <div>
      <h3
        className="mb-2 text-[10px] uppercase tracking-[0.18em] text-charcoal/55"
        style={{
          fontFamily:
            "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
        }}
      >
        Upgrade
      </h3>
      <div className="flex flex-col gap-2">
        {options.map((upgrade) => {
          const isSelected = selectedId === upgrade.id;
          const delta = Math.max(0, upgrade.price - basePrice);
          const label = formatSkuName({
            name: upgrade.name,
            vendor_name: upgrade.vendor_name,
            vendor_category: upgrade.vendor_category ?? undefined,
            is_voucher: upgrade.is_voucher,
          });
          return (
            <button
              key={upgrade.id}
              type="button"
              onClick={() => (isSelected ? onClear() : onSelect(upgrade))}
              aria-pressed={isSelected}
              className={`flex w-full items-center justify-between gap-3 rounded-xl border px-3.5 py-2.5 text-left text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-coral ${
                isSelected
                  ? "border-navy bg-navy/5 text-navy"
                  : "border-charcoal/15 bg-white text-navy hover:border-coral/40"
              }`}
              style={{ fontFamily: "DM Sans, sans-serif" }}
            >
              <span className="font-medium">
                {isSelected ? "Upgraded to " : "Upgrade to "}
                {label}
              </span>
              <span className="shrink-0 font-semibold text-coral">
                +${delta.toFixed(0)}
              </span>
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-xs text-charcoal/55">
        Upgrades replace your selected gift. They are not add-ons.
      </p>
    </div>
  );
}

/** Variant picker — pill row of variant_labels. Selected one is
 *  navy-filled; others outlined. Click updates the parent's
 *  selectedVariantId state which re-renders price + handleSendClick's
 *  target. Hidden entirely when variants.length === 1 (handled at
 *  the call site). */
function VariantPicker({
  variants,
  selectedId,
  isVoucher,
  onSelect,
}: {
  variants: SkuDetail[];
  selectedId: string;
  isVoucher: boolean;
  onSelect: (id: string) => void;
}) {
  const pickerLabel = isVoucher ? "Choose value" : "Pick a size";
  return (
    <div>
      <h3
        className="mb-2 text-[10px] uppercase tracking-[0.18em] text-charcoal/55"
        style={{
          fontFamily:
            "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
        }}
      >
        {pickerLabel}
      </h3>
      <div className="flex flex-wrap gap-1.5">
        {variants.map((v) => {
          const isSelected = v.id === selectedId;
          const label = v.variant_label ?? v.name;
          return (
            <button
              key={v.id}
              type="button"
              onClick={() => onSelect(v.id)}
              aria-pressed={isSelected}
              className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-coral ${
                isSelected
                  ? "border-navy bg-navy text-white"
                  : "border-navy/20 bg-white text-navy hover:border-navy"
              }`}
              style={{ fontFamily: "DM Sans, sans-serif" }}
            >
              <span className="font-medium">{label}</span>
              <span
                className={
                  isSelected ? "text-white/80" : "text-charcoal/55"
                }
              >
                ${v.price.toFixed(0)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Format a booking window for display. Input is two ISO strings;
 *  output is a human-friendly date + time range. Single-day windows
 *  collapse to "Sat 24 May · 2-4pm"; multi-day stay as
 *  "24-26 May". Kept local to avoid pulling in a new date library
 *  for one use case; Intl APIs are already supported everywhere. */
function formatBookingWindow(startISO: string, endISO: string): string {
  try {
    const start = new Date(startISO);
    const end = new Date(endISO);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return `${startISO} to ${endISO}`;
    }
    const sameDay =
      start.getFullYear() === end.getFullYear() &&
      start.getMonth() === end.getMonth() &&
      start.getDate() === end.getDate();
    const dateFmt = new Intl.DateTimeFormat("en-AU", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
    const timeFmt = new Intl.DateTimeFormat("en-AU", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    if (sameDay) {
      return `${dateFmt.format(start)} · ${timeFmt.format(start)}-${timeFmt.format(end)}`;
    }
    return `${dateFmt.format(start)} to ${dateFmt.format(end)}`;
  } catch {
    return `${startISO} – ${endISO}`;
  }
}
